import React from 'react';
import styled from 'styled-components';
import { PropTypes } from 'prop-types';
import Constants from '../../submodules/logictry_config/constants';
import Navigation from '../../services/Navigation';
import WindowSize from '../../services/WindowSize';
import UserAccount, { LOGGED_IN } from '../../services/UserAccount';
import Scroll from '../../services/Scroll';
import ScrollIt from '../../utils/Scroll';
import LogictryComponent from '../../submodules/logictry_config/LogictryComponent';
import { AngleLeftIcon, AngleRightIcon } from '../../styledhtml/Icon';
const Wrapper = styled.div`
  position: relative;
  overflow: hidden;
  > div {
    ${props => props.popupMaxHeight ? Constants.PopupMaxHeight : ''}
  }
`;
const InnerWrapper = styled.div`
  > div:last-child {
    transition: 0.5s all;
    opacity: 0;
    ${Constants.MediaMobileAndTablet} {
      display: none;
    }
  }
  :hover {
    > div:last-child {
      ${Constants.MediaDesktop} {
        opacity: 1;
      }
    }
  }
`;
const Arrows = styled.div`
  pointer-events: none;

  > div {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 2;
    pointer-events: auto;

    > div {
      border-radius: 50%;
      height: 2rem;
      width: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      background: ${Constants.isDarkMode ? Constants.SurfaceColor : 'rgba(255, 255, 255, 0.94)'};
      border: 1px solid ${Constants.isDarkMode ? Constants.LightBorderColor : 'rgba(0, 0, 0, 0.08)'};
      box-shadow: ${Constants.isDarkMode
        ? '0 2px 10px rgba(255, 255, 255, 0.06)'
        : '0 2px 10px rgba(0, 0, 0, 0.1)'};
      transition: background 0.12s ease, transform 0.12s ease, box-shadow 0.12s ease;
    }

    &:hover > div {
      background: ${Constants.isDarkMode ? '#2a2a2a' : '#fff'};
      transform: scale(1.05);
      box-shadow: ${Constants.isDarkMode
        ? '0 4px 14px rgba(255, 255, 255, 0.08)'
        : '0 4px 14px rgba(0, 0, 0, 0.12)'};
    }
  }

  > div:first-child {
    left: 0.35rem;
  }

  > div:last-child {
    right: 0.35rem;
  }
`;

// This makes it scroll to top on refresh
function resetScroll() {
  if (window.history?.state) {
    const currentState = JSON.parse(JSON.stringify(window.history.state)) || {};
    Object.keys(window.history.state).forEach((key) => {
      if (!['logicbase', 'tree'].find((prefix) => key.startsWith(prefix))) return;
      currentState[key] = 0;
    });
    window.history.replaceState(currentState, document.title);
  }
}
UserAccount.onStateUpdate((_, state) => {
  if (state === LOGGED_IN) {
    resetScroll();
  }
});
resetScroll();

// This function replaces the current state
const NATIVE_BODY = 'nativebody';
const SCROLL_ARRIVE_EPSILON = 1;

export default class Scrollable extends LogictryComponent {
  static propTypes = {
    children: PropTypes.any,
    style: PropTypes.object,
    onScroll: PropTypes.func,
    onKeyDown: PropTypes.func,
    onKeyUp: PropTypes.func,
    scrollRef: PropTypes.func,
    vertical: PropTypes.bool,
    horizontal: PropTypes.bool,
    rememberScrollPosition: PropTypes.string,
    id: PropTypes.string,
    paginationHeight: PropTypes.any,
    onPagination: PropTypes.func,
    disableFlex: PropTypes.bool,
    popupMaxHeight: PropTypes.bool,
    adjustForScrollbar: PropTypes.bool,
    hideScrollbar: PropTypes.bool,
    showScrollButtons: PropTypes.bool,
  }
  state = {
    showLeft: false,
    showRight: false,
  }
  scrollStarted = null
  scrollRestoreId = 0
  abortScrollRestore = false
  scrollRestoreAbortEls = new Set()
  componentDidMount() {
    const { paginationHeight } = this.props;
    if (paginationHeight) document.addEventListener('scroll', this.onBodyScroll);
    Navigation.onStateUpdate(this.navigationUpdate);
    WindowSize.onStateUpdate(this.windowSizeUpdated);
    Scroll.onStateUpdate(this);
  }
  componentDidUpdate(prevProps) {
    // Keep document scroll listener in sync when paginationHeight toggles (e.g. hasMore settles).
    const had = !!prevProps.paginationHeight;
    const has = !!this.props.paginationHeight;
    if (had === has) return;
    if (has) document.addEventListener('scroll', this.onBodyScroll);
    else document.removeEventListener('scroll', this.onBodyScroll);
  }
  componentWillUnmount() {
    // Always remove — paginationHeight may have toggled off while mounted.
    document.removeEventListener('scroll', this.onBodyScroll);
    if (this.nativeScrollRef) this.nativeScrollRef.removeEventListener('scroll', this.onNativeScroll);
    this.unbindAllScrollRestoreAbort();
    this.clearScrollRestore();
    Navigation.offStateUpdate(this.navigationUpdate);
    WindowSize.offStateUpdate(this.windowSizeUpdated);
    Scroll.offStateUpdate(this);
    if (this.observer) this.observer.disconnect();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    cancelAnimationFrame(this.paginationRaf);
    clearTimeout(this.replaceHistoryTimeout);
  }
  clearScrollRestore = () => {
    cancelAnimationFrame(this.scrollTopRaf);
    cancelAnimationFrame(this.scrollLeftRaf);
    this.scrollTopRaf = null;
    this.scrollLeftRaf = null;
  }
  beginScrollRestore = () => {
    this.abortScrollRestore = false;
    this.scrollRestoreId += 1;
    this.scrollStarted = Date.now();
    this.clearScrollRestore();
  }
  onUserScrollIntent = () => {
    if (this.abortScrollRestore) return;
    this.abortScrollRestore = true;
    this.clearScrollRestore();
  }
  bindScrollRestoreAbort = (el) => {
    if (!el || this.scrollRestoreAbortEls.has(el)) return;
    el.addEventListener('wheel', this.onUserScrollIntent, { passive: true });
    el.addEventListener('pointerdown', this.onUserScrollIntent, { passive: true });
    this.scrollRestoreAbortEls.add(el);
  }
  unbindScrollRestoreAbort = (el) => {
    if (!el || !this.scrollRestoreAbortEls.has(el)) return;
    el.removeEventListener('wheel', this.onUserScrollIntent);
    el.removeEventListener('pointerdown', this.onUserScrollIntent);
    this.scrollRestoreAbortEls.delete(el);
  }
  unbindAllScrollRestoreAbort = () => {
    [...this.scrollRestoreAbortEls].forEach((el) => this.unbindScrollRestoreAbort(el));
  }
  isRestoringScroll = () => (
    !this.abortScrollRestore
    && this.scrollStarted
    && (Date.now() - this.scrollStarted) < 2000
  )
  replaceHistory = (rememberScrollPosition, vertical, horizontal, { ignoreRestore } = {}) => {
    clearTimeout(this.replaceHistoryTimeout);
    if (!rememberScrollPosition) return;
    if (!ignoreRestore && this.isRestoringScroll()) return;
    // Empty rail reports 0 — don't clobber a saved offset before layout/restore.
    // Native body persist is ion-content, not this scrollport — skip the overflow check there.
    if (this.scrollRef && rememberScrollPosition !== NATIVE_BODY) {
      if (horizontal === 0 && this.scrollRef.scrollWidth <= this.scrollRef.clientWidth + 1) {
        horizontal = undefined;
      }
      if (vertical === 0 && this.scrollRef.scrollHeight <= this.scrollRef.clientHeight + 1) {
        vertical = undefined;
      }
    }
    if (vertical === undefined && horizontal === undefined) return;
    const href = window.location.href;
    this.replaceHistoryTimeout = setTimeout(() => {
      if (window.location.href !== href) return;
      const currentState = JSON.parse(JSON.stringify(window.history.state)) || {};
      let modified = false;
      if (vertical !== undefined && currentState[`${rememberScrollPosition}_vertical`] !== vertical) {
        modified = true;
        currentState[`${rememberScrollPosition}_vertical`] = vertical;
      }
      if (horizontal !== undefined && currentState[`${rememberScrollPosition}_horizontal`] !== horizontal) {
        modified = true;
        currentState[`${rememberScrollPosition}_horizontal`] = horizontal;
      }
      if (modified) {
        window.history.replaceState(currentState, document.title);
      }
    }, 100);
  }
  windowSizeUpdated = () => {
    if (!this.scrollRef) return;
    smoothscrollfix(this.scrollRef);
  }
  navigationUpdate = (_, state) => {
    if (state === 'SCROLL_TOP') {
      this.beginScrollRestore();
      const restoreId = this.scrollRestoreId;
      this.scrollTop(this.scrollRef, 0, restoreId);
      this.scrollLeft(this.scrollRef, 0, restoreId);
      const { rememberScrollPosition } = this.props;
      if (!rememberScrollPosition) return;
      this.replaceHistory(rememberScrollPosition, 0, 0, { ignoreRestore: true });
    }
  }
  onNativeScroll = () => {
    const { paginationHeight, onPagination, vertical, horizontal } = this.props;
    if (this.nativeScrollRef) {
      const { height, width } = this.nativeScrollRef.getBoundingClientRect();
      const { scrollTop, scrollHeight, scrollLeft, scrollWidth } = this.nativeScrollRef;
      // Own scrollport: when content overflows, pagination is driven by this element — not document scroll.
      if (vertical && scrollHeight > height) {
        if (height > (paginationHeight) && (scrollTop + paginationHeight) > (scrollHeight - height) && scrollHeight !== this.lastGetMoreScrollHeight) {
          this.lastGetMoreScrollHeight = scrollHeight;
          if (onPagination) onPagination();
        }
      }
      if (horizontal && scrollWidth > width) {
        if (width > (paginationHeight) && (scrollLeft + paginationHeight) > (scrollWidth - width) && scrollWidth !== this.lastGetMoreScrollWidth) {
          this.lastGetMoreScrollWidth = scrollWidth;
          if (onPagination) onPagination();
        }
      }
      this.replaceHistory(NATIVE_BODY, scrollTop);
    }
  }
  onBodyScroll = () => {
    const { paginationHeight, onPagination, vertical } = this.props;
    if (!paginationHeight) return;
    if (!this.scrollRef) return;
    const { height, bottom } = this.scrollRef.getBoundingClientRect();
    // Skip when this scrollport itself overflows — onScroll owns pagination then.
    if (this.scrollRef.scrollHeight > this.scrollRef.clientHeight) return;
    if (vertical && height > (paginationHeight) && (window.innerHeight + paginationHeight) > bottom && height !== this.lastGetMoreBodyHeight) {
      this.lastGetMoreBodyHeight = height;
      if (onPagination) onPagination();
    }
  }
  checkScrollButtons = () => {
    if (!this.scrollRef) return;
    const showLeft = this.scrollRef.scrollLeft > 0;
    const showRight = Math.ceil(this.scrollRef.scrollLeft + this.scrollRef.offsetWidth) < this.scrollRef.scrollWidth;
    this.setState({ showLeft, showRight });
  }
  onScroll = (e) => {
    const { paginationHeight, onPagination, rememberScrollPosition, vertical, horizontal } = this.props;
    if (this.scrollRef) {
      const { height, width } = this.scrollRef.getBoundingClientRect();
      const { scrollTop, scrollHeight, scrollLeft, scrollWidth } = this.scrollRef;
      if (vertical && height > paginationHeight && (scrollTop + paginationHeight) > (scrollHeight - height) && scrollHeight !== this.lastGetMoreScrollHeight) {
        this.lastGetMoreScrollHeight = scrollHeight;
        if (onPagination) onPagination();
      }
      if (horizontal && width > paginationHeight && (scrollLeft + paginationHeight) > (scrollWidth - width) && scrollWidth !== this.lastGetMoreScrollWidth) {
        this.lastGetMoreScrollWidth = scrollWidth;
        if (onPagination) onPagination();
      }
      this.checkScrollButtons();
    }
    if (!e || !rememberScrollPosition) return;
    this.replaceHistory(rememberScrollPosition, vertical ? e.target.scrollTop : undefined, horizontal ? e.target.scrollLeft : undefined);
  }
  onScrollRef = (e) => {
    const { rememberScrollPosition, vertical, horizontal } = this.props;
    if (!e || !rememberScrollPosition) return;
    this.beginScrollRestore();
    const restoreId = this.scrollRestoreId;

    // Check if any were set by { state } or { usr } for router set
    const state = window.history?.state || {};
    const internalState = window.history?.state?.state || window.history?.state?.usr || {};
    if (vertical) {
      const scrollTop = internalState[`${rememberScrollPosition}_vertical`] ?? state[`${rememberScrollPosition}_vertical`] ?? 0;
      if (scrollTop) this.scrollTop(e, scrollTop, restoreId);
    }
    if (horizontal) {
      const scrollLeft = internalState[`${rememberScrollPosition}_horizontal`] ?? state[`${rememberScrollPosition}_horizontal`] ?? 0;
      if (scrollLeft) this.scrollLeft(e, scrollLeft, restoreId);
    }

    if (Constants.isIphone) {
      document.querySelector('ion-content')?.getScrollElement().then((el) => {
        if (!el) return;
        if (this.nativeScrollRef && this.nativeScrollRef !== el) {
          this.nativeScrollRef.removeEventListener('scroll', this.onNativeScroll);
          this.unbindScrollRestoreAbort(this.nativeScrollRef);
        }
        const scrollTop = internalState[`${NATIVE_BODY}_vertical`] ?? state[`${NATIVE_BODY}_vertical`] ?? 0;
        this.scrollTop(el, scrollTop, restoreId);
        el.addEventListener('scroll', this.onNativeScroll);
        this.bindScrollRestoreAbort(el);
        this.nativeScrollRef = el;
      });
    }
  }
  scrollTop = (e, scrollTop, restoreId = this.scrollRestoreId) => {
    if (!e || this.abortScrollRestore || restoreId !== this.scrollRestoreId) return;
    if (Math.abs(e.scrollTop - scrollTop) <= SCROLL_ARRIVE_EPSILON || (Date.now() - this.scrollStarted) > 2000) {
      return;
    }
    if ((e.scrollHeight - e.clientHeight) >= scrollTop - SCROLL_ARRIVE_EPSILON) {
      e.scrollTop = scrollTop;
    }
    this.scrollTopRaf = requestAnimationFrame(() => this.scrollTop(e, scrollTop, restoreId));
  }
  scrollLeft = (e, scrollLeft, restoreId = this.scrollRestoreId) => {
    if (!e || this.abortScrollRestore || restoreId !== this.scrollRestoreId) return;
    if (Math.abs(e.scrollLeft - scrollLeft) <= SCROLL_ARRIVE_EPSILON || (Date.now() - this.scrollStarted) > 2000) {
      return;
    }
    const maxScroll = e.scrollWidth - e.clientWidth;
    if (maxScroll >= scrollLeft - SCROLL_ARRIVE_EPSILON) {
      e.scrollLeft = scrollLeft;
    } else if (this.props.onPagination && e.scrollWidth !== this.lastRestorePaginationWidth) {
      // Saved position is past the currently loaded page — keep paging until
      // the rail is wide enough (or the restore timeout elapses).
      this.lastRestorePaginationWidth = e.scrollWidth;
      this.scrollStarted = Date.now();
      this.props.onPagination();
    }
    this.scrollLeftRaf = requestAnimationFrame(() => this.scrollLeft(e, scrollLeft, restoreId));
  }
  getMarginOffsets = () => {
    const { adjustForScrollbar, horizontal, vertical } = this.props;
    let marginRight = '0px';
    let marginBottom = '0px';
    if (adjustForScrollbar && this.scrollRef && this.innerChildRef) {
      const scrollbarWidth = this.scrollRef.getBoundingClientRect().width - this.scrollRef.clientWidth;
      const scrollbarHeight = this.scrollRef.getBoundingClientRect().height - this.scrollRef.clientHeight;
      if (vertical && scrollbarWidth > 0) {
        marginRight = -scrollbarWidth;
      }
      if (horizontal && scrollbarHeight > 0) {
        marginBottom = -scrollbarHeight;
      }
    }
    return { marginRight, marginBottom };
  }
  checkPaginationNeeded = () => {
    cancelAnimationFrame(this.paginationRaf);
    this.paginationRaf = requestAnimationFrame(() => {
      const { onPagination, vertical, horizontal } = this.props;
      if (!onPagination || !this.scrollRef) {
        return;
      }
      const { scrollHeight, clientHeight, scrollWidth, clientWidth } = this.scrollRef;
      // Mutations fire constantly while a short list is settling; without the same
      // repeat guard onScroll uses, one dead-end page turns into a request per mutation.
      if (vertical && scrollHeight > 0 && scrollHeight <= clientHeight) {
        if (scrollHeight !== this.lastGetMoreContentHeight) {
          this.lastGetMoreContentHeight = scrollHeight;
          onPagination();
        }
      }
      if (horizontal && scrollWidth > 0 && scrollWidth <= clientWidth) {
        if (scrollWidth !== this.lastGetMoreContentWidth) {
          this.lastGetMoreContentWidth = scrollWidth;
          onPagination();
        }
      }
    });
  }
  mutationUpdate = () => {
    this.checkPaginationNeeded();

    // Check for scrollbar offset needed
    const { marginRight, marginBottom } = this.getMarginOffsets();
    this.innerChildRef.style.marginRight = `${marginRight}px`;
    this.innerChildRef.style.marginBottom = `${marginBottom}px`;
  }
  scroll = (forward) => {
    if (!this.scrollRef) return;
    const destination = Math.max(this.scrollRef.scrollLeft + (forward ? this.scrollRef.offsetWidth : -this.scrollRef.offsetWidth), 0);
    ScrollIt(destination, this.scrollRef, 500, 'easeInOutQuad', undefined, true);
  }
  render() {
    const { showLeft, showRight } = this.state;
    const { allowScroll } = Scroll;
    const { children, style = {}, onKeyDown, onKeyUp, horizontal, vertical, onScroll, scrollRef, id, disableFlex, popupMaxHeight, hideScrollbar, showScrollButtons } = this.props;
    const outerStyle = {
      overflow: 'hidden'
    };
    const middleStyle = {
      overflowX: (horizontal && allowScroll) ? 'auto' : 'hidden',
      overflowY: (vertical && allowScroll) ? 'auto' : 'hidden',
      display: 'flex',
      flexDirection: 'column',
      inset: 0,
      scrollbarWidth: 'thin',
      scrollbarColor: '#999 transparent',
    };
    const innerStyle = {
      flex: disableFlex ? null : 1,
    };
    if (hideScrollbar) {
      if (horizontal) {
        middleStyle.marginBottom = -40;
        middleStyle.paddingBottom = 40;
      }
      if (vertical) {
        middleStyle.marginRight = -40;
        middleStyle.paddingRight = 40;
      }
    }
    ['position'].forEach((v) => {
      if (style[v] !== undefined) {
        outerStyle[v] = style[v];
        middleStyle[v] = style[v];
      }
    });
    [ 'inset', 'top', 'right', 'bottom', 'left', 'height', 'width', 'background'].forEach((v) => {
      if (style[v] !== undefined) {
        outerStyle[v] = style[v];
      }
    });
    [ 'flex', 'flexGrow', 'flexShrink', 'minHeight'].forEach((v) => {
      if (style[v] !== undefined) {
        outerStyle[v] = style[v];
      }
    });
    [ 'maxHeight'].forEach((v) => {
      if (style[v] !== undefined) {
        middleStyle[v] = style[v];
      }
    });
    if (vertical && (style.flex !== undefined || style.flexGrow !== undefined)) {
      outerStyle.display = 'flex';
      outerStyle.flexDirection = 'column';
      middleStyle.flex = 1;
      middleStyle.minHeight = 0;
    } else if (style.height !== undefined || style.minHeight !== undefined) {
      if (style.height !== undefined) middleStyle.height = style.height;
      if (style.minHeight !== undefined) middleStyle.minHeight = style.minHeight;
    }
    [ 'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'display', 'alignItems', 'justifyContent', 'flexDirection', 'flexWrap'].forEach((v) => {
      if (style[v] !== undefined) {
        innerStyle[v] = style[v];
      }
    });
    return (
      <Wrapper
        style={outerStyle}
        popupMaxHeight={popupMaxHeight}
        onMouseEnter={this.checkScrollButtons}
      >
        <div
          id={id}
          style={middleStyle}
          onScroll={(e) => {
            this.onScroll(e);
            if (onScroll) onScroll(e);
          }}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          ref={(e) => {
            if (!e) return;
            if (e === this.scrollRef) return;
            if (this.scrollRef) this.unbindScrollRestoreAbort(this.scrollRef);
            this.scrollRef = e;
            this.bindScrollRestoreAbort(e);
            this.windowSizeUpdated();
            this.onScrollRef(e);
            if (scrollRef) scrollRef(e);
            this.mutationUpdate();
          }}
        >
          <InnerWrapper
            ref={(e) => {
              if (!e) return;
              if (e === this.innerChildRef) return;
              this.innerChildRef = e;
              if (this.observer) this.observer.disconnect();
              this.observer = new MutationObserver(this.mutationUpdate);
              this.observer.observe(e.parentNode, {
                childList: true,
                subtree: true,
              });
              if (this.resizeObserver) this.resizeObserver.disconnect();
              this.resizeObserver = new ResizeObserver(this.checkPaginationNeeded);
              this.resizeObserver.observe(e);
              this.mutationUpdate();
            }}
            style={innerStyle}
          >
            {children}
            {showScrollButtons && <Arrows>
              <div
                onClick={() => this.scroll(false)}
                style={{ display: showLeft && 'flex' || 'none' }}
              >
                <div>
                  <AngleLeftIcon style={{ width: 14, height: 14 }} />
                </div>
              </div>
              <div
                onClick={() => this.scroll(true)}
                style={{ display: showRight && 'flex' || 'none' }}
              >
                <div>
                  <AngleRightIcon style={{ width: 14, height: 14 }} />
                </div>
              </div>
            </Arrows> || <div></div>}
          </InnerWrapper>
        </div>
      </Wrapper>
    );
  }
}

function smoothscrollfix(ref) {
  if (!ref) return;
  if (!WindowSize.mobile || !WindowSize.isSafari) {
    ref.onscroll = null;
    return;
  }
  // Fixes the scroll at top problem ios
  let timeout;
  checkBounds();
  ref.onscroll = () => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = null;
      checkBounds();
    }, 10);
  }
  function checkBounds() {
    if (isNotScrollable()) return;
    if (isTop()) {
      ref.scrollTop = 1;
    } else if (isBottom()) {
      ref.scrollTop -= 1;
    }
  }
  function isNotScrollable() {
    return ref.offsetHeight === ref.scrollHeight;
  }
  function isTop() {
    return ref.scrollTop === 0;
  }
  function isBottom() {
    return (ref.scrollTop + ref.clientHeight) === ref.scrollHeight;
  }
}
